import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getRomeoJobs, getSearchSessions, getSearchSessionResults, submitCustomSearchMatchFeedback } from '../services/api';
import CandidateProfileModal from './CandidateProfileModal';
import './MatchmakerOverview.css';
import './Matchmaker.css';

const PAGE_SIZE = 10;

const normalizeCustomSearchMatch = (m) => ({
  ...m,
  feedback: m.feedback === 'none' ? null : m.feedback,
});
const normalizeCustomSearchMatchList = (list) => (list || []).map(normalizeCustomSearchMatch);

const SORT_OPTIONS = [
  { value: 'match_date', label: 'Match Date (newest first)' },
  { value: 'match_date_asc', label: 'Match Date (oldest first)' },
  { value: 'score', label: 'Top Score (highest first)' },
  { value: 'score_asc', label: 'Top Score (lowest first)' },
  { value: 'company_asc', label: 'Company (A–Z)' },
  { value: 'company_desc', label: 'Company (Z–A)' },
  { value: 'posted_at', label: 'Posted Date (newest first)' },
  { value: 'posted_at_asc', label: 'Posted Date (oldest first)' },
];

function MatchmakerOverview() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') === 'custom' ? 'custom' : 'romeo';

  // ---- Romeo tab state ----
  const [jobs, setJobs] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState('match_date');

  // ---- Custom searches tab state ----
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [sessionsError, setSessionsError] = useState(null);
  const [expandedSessionId, setExpandedSessionId] = useState(null);
  const [sessionMatches, setSessionMatches] = useState({}); // { [sessionId]: matches[] }
  const [sessionMatchesLoading, setSessionMatchesLoading] = useState({});
  const [modalMatch, setModalMatch] = useState(null);
  const [thumbsDownModal, setThumbsDownModal] = useState({ open: false, sessionId: null, matchId: null });
  const [thumbsDownReason, setThumbsDownReason] = useState('');

  const loadSessions = useCallback(async () => {
    setSessionsLoading(true);
    setSessionsError(null);
    try {
      const data = await getSearchSessions(50);
      setSessions(data.sessions || []);
    } catch (err) {
      setSessionsError(err.message || 'Failed to load searches');
    } finally {
      setSessionsLoading(false);
    }
  }, []);

  useEffect(() => { setCurrentPage(1); }, [sortBy]);
  useEffect(() => { fetchJobs(currentPage); }, [currentPage, sortBy]);

  // Load custom-search sessions on mount so the tab count is correct before the user opens the tab.
  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  useEffect(() => {
    const onSearchDone = () => {
      loadSessions();
    };
    window.addEventListener('customSearchComplete', onSearchDone);
    return () => window.removeEventListener('customSearchComplete', onSearchDone);
  }, [loadSessions]);

  const expandParam = searchParams.get('expand');

  // Deep link: /matchmaker?tab=custom&expand=<sessionId> opens that search and loads results.
  useEffect(() => {
    if (!expandParam || sessionsLoading) return;
    const session = sessions.find((s) => String(s.id) === String(expandParam));
    if (!session) return;

    const sid = session.id;
    setExpandedSessionId(sid);
    if (sessionMatches[sid] !== undefined) return;

    let cancelled = false;
    setSessionMatchesLoading((prev) => ({ ...prev, [sid]: true }));
    getSearchSessionResults(sid)
      .then((data) => {
        if (cancelled) return;
        setSessionMatches((prev) => ({ ...prev, [sid]: normalizeCustomSearchMatchList(data.matches) }));
      })
      .catch(() => {
        if (cancelled) return;
        setSessionMatches((prev) => ({ ...prev, [sid]: [] }));
      })
      .finally(() => {
        if (cancelled) return;
        setSessionMatchesLoading((prev) => ({ ...prev, [sid]: false }));
      });

    return () => {
      cancelled = true;
    };
  }, [expandParam, sessions, sessionsLoading, sessionMatches]);

  const toggleSession = useCallback(async (sessionId) => {
    if (expandedSessionId === sessionId) {
      setExpandedSessionId(null);
      return;
    }
    setExpandedSessionId(sessionId);
    if (sessionMatches[sessionId]) return; // already loaded

    setSessionMatchesLoading((prev) => ({ ...prev, [sessionId]: true }));
    try {
      const data = await getSearchSessionResults(sessionId);
      setSessionMatches((prev) => ({ ...prev, [sessionId]: normalizeCustomSearchMatchList(data.matches) }));
    } catch (err) {
      setSessionMatches((prev) => ({ ...prev, [sessionId]: [] }));
    } finally {
      setSessionMatchesLoading((prev) => ({ ...prev, [sessionId]: false }));
    }
  }, [expandedSessionId, sessionMatches]);

  const parseSort = (val) => {
    if (val.endsWith('_asc')) return { sort_by: val.replace(/_asc$/, ''), sort_order: 'asc' };
    if (val.endsWith('_desc')) return { sort_by: val.replace(/_desc$/, ''), sort_order: 'desc' };
    return { sort_by: val, sort_order: 'desc' };
  };

  const fetchJobs = async (page) => {
    try {
      setLoading(true);
      const { sort_by, sort_order } = parseSort(sortBy);
      const data = await getRomeoJobs({
        status: 'matched',
        page,
        page_size: PAGE_SIZE,
        sort_by,
        sort_order,
      });
      setJobs(data.results || []);
      setTotalCount(data.count || 0);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const getPageNumbers = () => {
    const pages = [];
    const delta = 2;
    const left = currentPage - delta;
    const right = currentPage + delta;

    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || (i >= left && i <= right)) {
        pages.push(i);
      } else if (pages[pages.length - 1] !== '...') {
        pages.push('...');
      }
    }
    return pages;
  };

  const getStatusBadge = (status) => {
    const badges = {
      matching: { label: 'In Progress', color: '#f59e0b' },
      ready: { label: 'Matched', color: '#8b5cf6' },
      matched: { label: 'Matched', color: '#8b5cf6' },
      intent_received: { label: 'Intent Received', color: '#10b981' },
    };
    return badges[status] || { label: status, color: '#6b7280' };
  };

  const formatPostDate = (dateString) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatMatchDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const dateStr = date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
    const timeStr = date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
    return `Matched ${dateStr} at ${timeStr}`;
  };

  const formatSearchDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  };

  const getMatchPercentageClass = (pct) => {
    if (pct >= 90) return 'high';
    if (pct >= 80) return 'medium';
    return 'low';
  };

  const updateSessionMatchFeedback = (sessionId, matchId, feedback) => {
    setSessionMatches((prev) => {
      const list = prev[sessionId];
      if (!list) return prev;
      return {
        ...prev,
        [sessionId]: list.map((m) => (m.id === matchId ? { ...m, feedback } : m)),
      };
    });
  };

  const handleCustomFeedback = async (sessionId, matchId, feedbackType, notes = '') => {
    try {
      await submitCustomSearchMatchFeedback(matchId, feedbackType, notes);
      updateSessionMatchFeedback(sessionId, matchId, feedbackType);
    } catch (err) {
      console.error('Error submitting custom search feedback:', err);
    }
  };

  const handleCustomResetFeedback = async (sessionId, matchId) => {
    try {
      await submitCustomSearchMatchFeedback(matchId, null, '');
      updateSessionMatchFeedback(sessionId, matchId, null);
    } catch (err) {
      console.error('Error resetting custom search feedback:', err);
    }
  };

  const handleCustomThumbsDown = (sessionId, matchId) => {
    setThumbsDownReason('');
    setThumbsDownModal({ open: true, sessionId, matchId });
  };

  const handleThumbsDownConfirm = async () => {
    if (thumbsDownModal.sessionId == null || thumbsDownModal.matchId == null) return;
    await handleCustomFeedback(
      thumbsDownModal.sessionId,
      thumbsDownModal.matchId,
      'thumbs_down',
      thumbsDownReason
    );
    setThumbsDownModal({ open: false, sessionId: null, matchId: null });
    setThumbsDownReason('');
  };

  const handleThumbsDownSkip = async () => {
    if (thumbsDownModal.sessionId == null || thumbsDownModal.matchId == null) return;
    await handleCustomFeedback(
      thumbsDownModal.sessionId,
      thumbsDownModal.matchId,
      'thumbs_down',
      ''
    );
    setThumbsDownModal({ open: false, sessionId: null, matchId: null });
    setThumbsDownReason('');
  };

  if (loading && activeTab === 'romeo') {
    return (
      <div className="matchmaker-overview">
        <div className="matchmaker-overview-inner">
          <div className="loading">Loading jobs...</div>
        </div>
      </div>
    );
  }

  if (error && activeTab === 'romeo') {
    return (
      <div className="matchmaker-overview">
        <div className="matchmaker-overview-inner">
          <div className="error">Error: {error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="matchmaker-overview">
      {/* Candidate detail modal (for custom search results) */}
      {modalMatch && (
        <CandidateProfileModal
          match={modalMatch}
          candidate={modalMatch.candidate}
          job={null}
          onClose={() => setModalMatch(null)}
        />
      )}

      {thumbsDownModal.open && (
        <div
          className="feedback-modal-overlay"
          onClick={() => setThumbsDownModal({ open: false, sessionId: null, matchId: null })}
          role="presentation"
        >
          <div className="feedback-modal" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
            <div className="feedback-modal-header">
              <div className="feedback-modal-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
                  <path d="M15 3H6c-.83 0-1.54.5-1.84 1.22l-3.02 7.05c-.09.23-.14.47-.14.73v2c0 1.1.9 2 2 2h6.31l-.95 4.57-.03.32c0 .41.17.79.44 1.06L9.83 23l6.59-6.59c.36-.36.58-.86.58-1.41V5c0-1.1-.9-2-2-2zm4 0v12h4V3h-4z" fill="#ef4444" />
                </svg>
              </div>
              <h3 className="feedback-modal-title">Why is this a poor match?</h3>
              <p className="feedback-modal-subtitle">Your feedback helps improve future candidate ranking.</p>
            </div>
            <textarea
              className="feedback-modal-textarea"
              placeholder="e.g. Missing required practice area, insufficient experience, location mismatch..."
              value={thumbsDownReason}
              onChange={(e) => setThumbsDownReason(e.target.value)}
              rows={4}
            />
            <div className="feedback-modal-actions">
              <button type="button" className="feedback-modal-btn feedback-modal-btn--skip" onClick={handleThumbsDownSkip}>
                Skip
              </button>
              <button type="button" className="feedback-modal-btn feedback-modal-btn--confirm" onClick={handleThumbsDownConfirm}>
                Submit Feedback
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="matchmaker-overview-inner">
      {/* Header */}
      <div className="matchmaker-overview-header">
        <div className="header-icon">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 21.35L10.55 20.03C5.4 15.36 2 12.27 2 8.5C2 5.41 4.42 3 7.5 3C9.24 3 10.91 3.81 12 5.08C13.09 3.81 14.76 3 16.5 3C19.58 3 22 5.41 22 8.5C22 12.27 18.6 15.36 13.45 20.03L12 21.35Z" fill="currentColor"/>
          </svg>
        </div>
        <div className="header-text">
          <h1>Matchmaker — Jobs Ready for Review</h1>
          <p>Review AI-matched candidates and make hiring decisions</p>
        </div>
        <div className="header-actions">
          <div className="sort-control">
            <label htmlFor="matchmaker-sort" className="sort-label">Sort by</label>
            <select
              id="matchmaker-sort"
              className="sort-select"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              {SORT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div className="header-count-pill">
            {totalCount} job{totalCount !== 1 ? 's' : ''}
          </div>
        </div>
      </div>

      {/* Tab switcher */}
      <div className="mo-tabs">
        <button
          className={`mo-tab${activeTab === 'romeo' ? ' mo-tab--active' : ''}`}
          onClick={() => setSearchParams({})}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M20 6H16V4C16 2.89 15.11 2 14 2H10C8.89 2 8 2.89 8 4V6H4C2.89 6 2.01 6.89 2.01 8L2 19C2 20.11 2.89 21 4 21H20C21.11 21 22 20.11 22 19V8C22 6.89 21.11 6 20 6ZM10 4H14V6H10V4Z" fill="currentColor"/>
          </svg>
          Romeo Matches
          <span className="mo-tab-count">{totalCount}</span>
        </button>
        <button
          className={`mo-tab${activeTab === 'custom' ? ' mo-tab--active mo-tab--active-custom' : ''}`}
          onClick={() => setSearchParams({ tab: 'custom' })}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" fill="currentColor"/>
          </svg>
          Custom Searches
          <span className="mo-tab-count">{sessions.length}</span>
        </button>
      </div>

      {/* ===== ROMEO TAB ===== */}
      {activeTab === 'romeo' && (jobs.length === 0 ? (
        <div className="empty-state">
          <p>No jobs in the matching pipeline yet.</p>
        </div>
      ) : (
        <>
          <div className="jobs-list">
            {jobs.map((job) => {
              const badge = getStatusBadge(job.status);
              const showReady =
                (job.status === 'matched' || job.status === 'ready') && job.top_match_score != null;
              const showMatchDate = Boolean(job.latest_match_date);
              return (
                <article key={job.id} className="job-card-overview">
                  <div className="job-card-main">
                    <div className="job-card-content">
                      <div className="job-card-header">
                        <h3 className="job-title">{job.title}</h3>
                        <div className="job-card-badges">
                          {job.practice_area && (
                            <span className="badge badge-practice">{job.practice_area}</span>
                          )}
                          <span
                            className="badge badge-status"
                            style={{ backgroundColor: `${badge.color}18`, color: badge.color, borderColor: `${badge.color}40` }}
                          >
                            {badge.label}
                          </span>
                        </div>
                      </div>

                      <div className="job-meta">
                      <span className="meta-item">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <path d="M20 6H16V4C16 2.89 15.11 2 14 2H10C8.89 2 8 2.89 8 4V6H4C2.89 6 2.01 6.89 2.01 8L2 19C2 20.11 2.89 21 4 21H20C21.11 21 22 20.11 22 19V8C22 6.89 21.11 6 20 6ZM10 4H14V6H10V4Z" fill="currentColor"/>
                        </svg>
                        {job.company || 'Company'}
                      </span>
                      <span className="meta-item">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <path d="M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22C12 22 19 14.25 19 9C19 5.13 15.87 2 12 2ZM12 11.5C10.62 11.5 9.5 10.38 9.5 9C9.5 7.62 10.62 6.5 12 6.5C13.38 6.5 14.5 7.62 14.5 9C14.5 10.38 13.38 11.5 12 11.5Z" fill="currentColor"/>
                        </svg>
                        {job.location || 'Remote'}
                      </span>
                      {job.work_type && (
                        <span className="meta-item meta-work-type">{job.work_type}</span>
                      )}
                      {(job.posted_at || job.created_at) && (
                        <span className="meta-item">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                            <path d="M19 4H18V2H16V4H8V2H6V4H5C3.89 4 3.01 4.9 3.01 6L3 20C3 21.1 3.89 22 5 22H19C20.1 22 21 21.1 21 20V6C21 4.9 20.1 4 19 4ZM19 20H5V10H19V20ZM19 8H5V6H19V8ZM7 13H17V18H12V13Z" fill="currentColor"/>
                          </svg>
                          Posted {formatPostDate(job.posted_at || job.created_at)}
                        </span>
                      )}
                    </div>

                    <div className="job-card-actions-row">
                      <div className="job-stats">
                        {showMatchDate && (
                        <span className="match-date">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
                            <path d="M12 2C6.5 2 2 6.5 2 12S6.5 22 12 22 22 17.5 22 12 17.5 2 12 2M12 20C7.59 20 4 16.41 4 12S7.59 4 12 4 20 7.59 20 12 16.41 20 12 20M12.5 7H11V13L16.2 16.2L17 14.9L12.5 12.2V7Z" fill="#8b5cf6"/>
                          </svg>
                          {formatMatchDate(job.latest_match_date)}
                        </span>
                        )}
                        {showMatchDate && showReady && (
                          <span className="job-stats-sep" aria-hidden="true">·</span>
                        )}
                        {showReady && (
                        <span className="stat-ready">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
                            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="currentColor"/>
                          </svg>
                          Candidates Ready
                        </span>
                        )}
                      </div>
                      <button
                        type="button"
                        className="btn-view-matches"
                        onClick={() => navigate(`/matchmaker/${job.id}`)}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
                          <path d="M12 4.5C7 4.5 2.73 7.61 1 12C2.73 16.39 7 19.5 12 19.5C17 19.5 21.27 16.39 23 12C21.27 7.61 17 4.5 12 4.5ZM12 17C9.24 17 7 14.76 7 12C7 9.24 9.24 7 12 7C14.76 7 17 9.24 17 12C17 14.76 14.76 17 12 17ZM12 9C10.34 9 9 10.34 9 12C9 13.66 10.34 15 12 15C13.66 15 15 13.66 15 12C15 10.34 13.66 9 12 9Z" fill="currentColor"/>
                        </svg>
                        View Matches
                      </button>
                    </div>
                  </div>

                    <div className="job-card-score">
                      <span className="score-label">Top Match</span>
                      <span className="score-value">{job.top_match_score != null ? job.top_match_score : '--'}</span>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination">
              <button
                className="pagination-btn pagination-arrow"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" fill="currentColor"/>
                </svg>
              </button>

              {getPageNumbers().map((page, idx) =>
                page === '...' ? (
                  <span key={`ellipsis-${idx}`} className="pagination-ellipsis">…</span>
                ) : (
                  <button
                    key={page}
                    className={`pagination-btn pagination-num ${currentPage === page ? 'active' : ''}`}
                    onClick={() => setCurrentPage(page)}
                  >
                    {page}
                  </button>
                )
              )}

              <button
                className="pagination-btn pagination-arrow"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" fill="currentColor"/>
                </svg>
              </button>
            </div>
          )}
        </>
      ))}

      {/* ===== CUSTOM SEARCHES TAB ===== */}
      {activeTab === 'custom' && (
        <div className="mo-custom-tab">
          <div className="mo-custom-header">
            <p className="mo-custom-subtitle">
              Candidates found through your manual Pearch queries — not tied to any specific job.
            </p>
            <button className="mo-new-search-btn" onClick={() => navigate('/search')}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" fill="currentColor"/>
              </svg>
              New Search
            </button>
          </div>

          {sessionsLoading ? (
            <div className="loading">Loading searches…</div>
          ) : sessionsError ? (
            <div className="error">Error: {sessionsError}</div>
          ) : sessions.length === 0 ? (
            <div className="empty-state mo-custom-empty">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" className="mo-empty-icon">
                <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" fill="currentColor"/>
              </svg>
              <p>No custom searches yet.</p>
              <button className="mo-new-search-btn" onClick={() => navigate('/search')}>
                Run your first search
              </button>
            </div>
          ) : (
            <div className="mo-sessions-list">
              {sessions.map((session) => {
                const isExpanded = expandedSessionId === session.id;
                const matches = sessionMatches[session.id] || [];
                const matchesLoading = sessionMatchesLoading[session.id];

                return (
                  <div key={session.id} className={`mo-session-card${isExpanded ? ' mo-session-card--expanded' : ''}`}>
                    <button
                      className="mo-session-toggle"
                      onClick={() => toggleSession(session.id)}
                    >
                      <div className="mo-session-query">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className="mo-session-icon">
                          <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" fill="currentColor"/>
                        </svg>
                        <span
                          className="mo-session-query-text"
                          title={session.query}
                        >
                          "{session.query}"
                        </span>
                      </div>
                      <div className="mo-session-meta">
                        <span className="mo-session-count">
                          {session.result_count} candidate{session.result_count !== 1 ? 's' : ''}
                        </span>
                        <span className="mo-session-date">{formatSearchDate(session.created_at)}</span>
                        <svg
                          width="16" height="16" viewBox="0 0 24 24" fill="none"
                          className={`mo-session-chevron${isExpanded ? ' mo-session-chevron--open' : ''}`}
                        >
                          <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6z" fill="currentColor"/>
                        </svg>
                      </div>
                    </button>

                    {isExpanded && (
                      <div className="mo-session-results">
                        {matchesLoading ? (
                          <div className="mo-session-loading">Loading candidates…</div>
                        ) : matches.length === 0 ? (
                          <p className="mo-session-empty">No candidates stored for this search.</p>
                        ) : (
                          <div className="mo-candidate-grid">
                            {matches.map((match) => (
                              <div key={match.id} className={`mo-candidate-card${match.feedback === 'thumbs_down' ? ' mo-candidate-card--rejected' : ''}`}>
                                <div className="mo-candidate-top">
                                  <div className="mo-candidate-avatar">
                                    {match.candidate?.picture_url ? (
                                      <img
                                        src={match.candidate.picture_url}
                                        alt={match.candidate.full_name}
                                        onError={(e) => { e.target.style.display = 'none'; }}
                                      />
                                    ) : (
                                      <span>{match.candidate?.first_name?.[0] || '?'}</span>
                                    )}
                                  </div>
                                  <div className="mo-candidate-info">
                                    <div className="mo-candidate-name-row">
                                      <div className="mo-candidate-name">{match.candidate?.full_name || 'Unknown'}</div>
                                    </div>
                                  </div>
                                  <div className="mo-score-column">
                                    <div className="mo-score-pair">
                                      <div className="mo-pearch-score">
                                        <span className="mo-score-val">{match.pearch_score * 20}</span>
                                        <span className="mo-score-lbl">Pearch</span>
                                      </div>
                                      <div className="mo-custom-score">
                                        <span className="mo-score-val">
                                          {match.custom_score != null ? Math.round(match.custom_score) : '—'}
                                        </span>
                                        <span className="mo-score-lbl">Custom</span>
                                      </div>
                                    </div>
                                    <div className="confidence-badge">
                                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden>
                                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" fill="#8b5cf6" />
                                      </svg>
                                      High Confidence
                                    </div>
                                  </div>
                                </div>

                                <div className="mo-candidate-secondary">
                                  <p className="mo-candidate-meta mo-candidate-meta--block">
                                    <span className="mo-candidate-exp">
                                      {match.candidate?.total_experience_years || 0} years experience
                                    </span>
                                    <span className="mo-dot" aria-hidden> · </span>
                                    <span className="mo-candidate-location">{match.candidate?.location || '—'}</span>
                                  </p>
                                  {match.created_at && (
                                    <div className="mo-candidate-matched">
                                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
                                        <path d="M12 2C6.5 2 2 6.5 2 12S6.5 22 12 22 22 17.5 22 12 17.5 2 12 2M12 20C7.59 20 4 16.41 4 12S7.59 4 12 4 20 7.59 20 12 16.41 20 12 20M12.5 7H11V13L16.2 16.2L17 14.9L12.5 12.2V7Z" fill="#8b5cf6" />
                                      </svg>
                                      <span className="mo-candidate-matched-text">{formatMatchDate(match.created_at)}</span>
                                    </div>
                                  )}
                                </div>

                                {match.candidate?.expertise?.length > 0 && (
                                  <div className="mo-candidate-tags">
                                    {match.candidate.expertise.slice(0, 3).map((s, i) => (
                                      <span key={i} className="mo-tag">{s}</span>
                                    ))}
                                  </div>
                                )}

                                <div className="mo-match-metrics-panel">
                                  {[
                                    { label: 'Experience Match', value: match.experience_match },
                                    { label: 'Skills Alignment', value: match.skills_alignment },
                                    { label: 'Cultural Fit', value: match.cultural_fit },
                                  ].map(({ label, value }) => (
                                    <div key={label} className="mo-bar-row">
                                      <span className="mo-bar-label">{label}</span>
                                      <div className="mo-bar-track-wrap">
                                        <div className="mo-bar-track">
                                          <div
                                            className={`mo-bar-fill mo-bar-fill--${getMatchPercentageClass(value)}`}
                                            style={{ width: `${value}%` }}
                                          />
                                        </div>
                                      </div>
                                      <span className={`mo-bar-pct mo-bar-pct--${getMatchPercentageClass(value)}`}>{value}%</span>
                                    </div>
                                  ))}
                                </div>

                                <div className="match-feedback mo-candidate-card-feedback">
                                  <button
                                    type="button"
                                    className={`feedback-btn feedback-btn--up ${match.feedback === 'thumbs_up' ? 'active' : ''}`}
                                    onClick={() => handleCustomFeedback(session.id, match.id, 'thumbs_up')}
                                    disabled={!!match.feedback}
                                    title={match.feedback ? 'Feedback already submitted — reset to change' : 'Good match'}
                                  >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
                                      <path d="M1 21h4V9H1v12zm22-11c0-1.1-.9-2-2-2h-6.31l.95-4.57.03-.32c0-.41-.17-.79-.44-1.06L14.17 1 7.59 7.59C7.22 7.95 7 8.45 7 9v10c0 1.1.9 2 2 2h9c.83 0 1.54-.5 1.84-1.22l3.02-7.05c.09-.23.14-.47.14-.73v-2z" fill="currentColor" />
                                    </svg>
                                    Good match
                                  </button>
                                  <button
                                    type="button"
                                    className={`feedback-btn feedback-btn--down ${match.feedback === 'thumbs_down' ? 'active' : ''}`}
                                    onClick={() => handleCustomThumbsDown(session.id, match.id)}
                                    disabled={!!match.feedback}
                                    title={match.feedback ? 'Feedback already submitted — reset to change' : 'Poor match'}
                                  >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
                                      <path d="M15 3H6c-.83 0-1.54.5-1.84 1.22l-3.02 7.05c-.09.23-.14.47-.14.73v2c0 1.1.9 2 2 2h6.31l-.95 4.57-.03.32c0 .41.17.79.44 1.06L9.83 23l6.59-6.59c.36-.36.58-.86.58-1.41V5c0-1.1-.9-2-2-2zm4 0v12h4V3h-4z" fill="currentColor" />
                                    </svg>
                                    Poor match
                                  </button>
                                  {match.feedback && (
                                    <button
                                      type="button"
                                      className="feedback-btn feedback-btn--reset"
                                      onClick={() => handleCustomResetFeedback(session.id, match.id)}
                                      title="Reset feedback"
                                    >
                                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
                                        <path d="M12 5V1L7 6l5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z" fill="currentColor" />
                                      </svg>
                                      Reset
                                    </button>
                                  )}
                                </div>

                                <button
                                  className="mo-view-btn"
                                  onClick={() => setModalMatch(match)}
                                >
                                  View Details
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
      </div>
    </div>
  );
}

export default MatchmakerOverview;
